import { Pause, Play, X, Check, RotateCw, Play as PlayTriangle } from "lucide-react";
import { cn } from "@lib/utils";
import type { UploadSlot } from "~/lib/uploads";
import { formatBytesPerSecond, formatEta } from "~/lib/uploads/throughput";

interface UploadQueueRowProps {
  slot: UploadSlot;
  onPause: (id: string) => void;
  onResume: (id: string) => void;
  onCancel: (id: string) => void;
  onDismiss: (id: string) => void;
  onRetry: (id: string) => void;
}

// Dark near-black rounded thumbnail. Backed by the Tier-0 local preview frame
// when one was captured (shown from the instant the file is picked). Overlay
// differs by state: plain frame while uploading, a light play triangle when the
// transfer completed, a red "!" on failure.
function Thumbnail({
  status,
  previewUrl,
}: {
  status: UploadSlot["status"];
  previewUrl: string | null;
}) {
  return (
    <div
      data-testid="upload-thumbnail"
      className="relative flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-base-600"
    >
      {previewUrl && (
        <img
          data-testid="upload-thumbnail-preview"
          src={previewUrl}
          alt=""
          className="absolute inset-0 size-full object-cover"
        />
      )}
      {status === "processing" && (
        <PlayTriangle className="relative size-4 fill-white text-white" />
      )}
      {status === "error" && <span className="relative text-sm font-bold text-error-500">!</span>}
    </div>
  );
}

function statusLine(slot: UploadSlot): { text: string; className: string } {
  switch (slot.status) {
    case "processing":
      return { text: "Completed", className: "text-success-600" };
    case "finishing":
      return { text: "Finishing…", className: "text-primary-500" };
    case "error":
      return { text: slot.errorMessage ?? "Upload failed", className: "text-error-500" };
    case "paused":
      return { text: `Paused — ${slot.progress}%`, className: "text-neutral-500" };
    case "cancelled":
      return { text: "Cancelled", className: "text-neutral-500" };
    case "uploading": {
      const rate = slot.bytesPerSecond
        ? ` · ${formatBytesPerSecond(slot.bytesPerSecond)}`
        : "";
      const eta =
        slot.etaSeconds != null ? ` · ${formatEta(slot.etaSeconds)} left` : "";
      return {
        text: `Uploading — ${slot.progress}%${rate}${eta}`,
        className: "text-primary-500",
      };
    }
    default:
      return { text: "Queued", className: "text-neutral-500" };
  }
}

// Circular outlined icon button (~36px, thin gray border, white bg).
function CircleButton({
  onClick,
  label,
  children,
  tone = "neutral",
}: {
  onClick: () => void;
  label: string;
  children: React.ReactNode;
  tone?: "neutral" | "success" | "error";
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={cn(
        "flex size-9 shrink-0 items-center justify-center rounded-full border bg-neutral-white transition-colors",
        tone === "neutral" && "border-neutral-300 text-neutral-600 hover:border-neutral-500",
        tone === "success" && "border-success-300 text-success-600",
        tone === "error" && "border-neutral-300 text-neutral-600 hover:border-error-500 hover:text-error-500"
      )}
    >
      {children}
    </button>
  );
}

export function UploadQueueRow({
  slot,
  onPause,
  onResume,
  onCancel,
  onDismiss,
  onRetry,
}: UploadQueueRowProps) {
  const line = statusLine(slot);
  const isDone = slot.status === "processing";
  const isError = slot.status === "error";
  const isPaused = slot.status === "paused";
  const isFinishing = slot.status === "finishing";
  const isCancelled = slot.status === "cancelled";

  return (
    <div data-testid="upload-queue-row" data-status={slot.status} className="flex items-center gap-3">
      <Thumbnail status={slot.status} previewUrl={slot.previewUrl} />

      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-base-600" title={slot.name}>
          {slot.name}
        </div>
        <div className={cn("mt-0.5 text-xs", line.className)} data-testid="upload-status-line">
          {line.text}
        </div>
        <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-neutral-200">
          {/* Finishing: bar full but NOT green — completion isn't confirmed
              yet — pulsing as the indeterminate affordance. */}
          <div
            data-testid="upload-progress-fill"
            className={cn(
              "h-full rounded-full transition-[width] duration-300",
              isDone ? "bg-success-500" : "bg-primary-500",
              isFinishing && "animate-pulse"
            )}
            style={{ width: `${isDone || isFinishing ? 100 : slot.progress}%` }}
          />
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {isDone ? (
          <>
            <CircleButton label="Completed" onClick={() => onDismiss(slot.id)} tone="success">
              <Check className="size-4" />
            </CircleButton>
            <CircleButton label="Dismiss" onClick={() => onDismiss(slot.id)}>
              <X className="size-4" />
            </CircleButton>
          </>
        ) : isError ? (
          <>
            {/* A pick-time rejection has no Uppy file behind it — nothing to
                restart, so retry is hidden and only dismiss remains. */}
            {slot.retryable !== false && (
              <CircleButton label="Retry" onClick={() => onRetry(slot.id)}>
                <RotateCw className="size-4" />
              </CircleButton>
            )}
            <CircleButton label="Dismiss" onClick={() => onDismiss(slot.id)}>
              <X className="size-4" />
            </CircleButton>
          </>
        ) : isCancelled ? (
          // Nothing left to control — the transfer is aborted. X clears the row.
          <CircleButton label="Dismiss" onClick={() => onDismiss(slot.id)}>
            <X className="size-4" />
          </CircleButton>
        ) : isFinishing ? (
          // No parts are in flight during finishing, so pause is meaningless.
          <CircleButton label="Cancel" onClick={() => onCancel(slot.id)}>
            <X className="size-4" />
          </CircleButton>
        ) : (
          <>
            {isPaused ? (
              <CircleButton label="Resume" onClick={() => onResume(slot.id)}>
                <Play className="size-4" />
              </CircleButton>
            ) : (
              <CircleButton label="Pause" onClick={() => onPause(slot.id)}>
                <Pause className="size-4" />
              </CircleButton>
            )}
            <CircleButton label="Cancel" onClick={() => onCancel(slot.id)}>
              <X className="size-4" />
            </CircleButton>
          </>
        )}
      </div>
    </div>
  );
}
